import Vue from 'vue';
import VueRouter from 'vue-router';
import config from '@/config';
import authService from '@/service/auth.service';
import autoRefreshService from '@/service/auto-refresh.service';
import analyticsService from '@/service/analytics.service';
import esApiV3 from '@/service/esapiV3.service';
import { getSignedUrl } from '@/plugins/s3';
import { useManufacturerStore } from '@/stores/manufacturerStore';
import { useAssetTypesStore } from '@/stores/assetTypesStore';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { useAuthStore } from '@/stores/authStore';
import { useGlobalStore } from '@/stores/globalStore';

const WarrantyReviewLayout = () =>
  import('@/components/warranty/warranty-review-layout.vue');
const PageNotFound = () => import('@/components/404/404.vue');
const LoginLayout = () => import('@/views/login-layout.vue');
const PortalLayout = () => import('@/views/portal-layout.vue');
const MaintenanceLayout = () => import('@/views/maintenance.vue');

Vue.use(VueRouter);

/**
 * @param {string} v5Path
 * @param {import('vue-router').Route} v2Route
 */
function mapToV5Url(v5Path, v2Route) {
  const newUrl = `${config.bohV5Url}${v5Path}`;

  const paramKeys = Object.keys(v2Route.query);
  if (paramKeys.length === 0) {
    return newUrl;
  }

  const params = new URLSearchParams();
  paramKeys.forEach((key) => {
    const value = v2Route.query[key];
    // v5 wants arrays to be passed as multiple params with the same key instead of a comma separated list
    if (Array.isArray(value)) {
      value.forEach((v) => {
        params.append(key, v);
      });
    } else {
      params.append(key, v2Route.query[key]);
    }
  });

  return `${newUrl}?${params.toString()}`;
}

/**
 * @type {import('vue-router').RouteConfig[]}
 */
const routes = [
  {
    path: '/login',
    component: LoginLayout,
    props: (route) => ({ nextRoute: route.query.nextRoute }),
    name: 'Login',
    beforeEnter: (to, from, next) => {
      const globalStore = useGlobalStore();
      globalStore.resetState();
      authService.clearAuthToken();
      next();
    },
  },
  {
    path: '*',
    name: 'PageNotFound',
    component: PageNotFound,
  },
  {
    path: '/maintenance',
    name: 'Maintenance',
    component: MaintenanceLayout,
  },
  {
    path: '/',
    component: PortalLayout,
    name: 'PortalLayout',
    redirect: '/taskboard',
    children: [
      {
        path: 'taskboard',
        name: 'Taskboard',
        beforeEnter: (to, _from, next) => {
          if (to.path === '/taskboard') {
            window.location.href = mapToV5Url('/taskboard', to);
          } else {
            next();
          }
        },
        children: [
          {
            path: 'task-list',
            name: 'TaskList',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url('/taskboard/task-list', to);
            },
          },
          {
            path: 'assigned-tasks',
            name: 'AssignedTasks',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                '/taskboard/assigned-tasks',
                to,
              );
            },
          },
          {
            path: 'all-tasks',
            name: 'AllTasks',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url('/taskboard/all-tasks', to);
            },
          },
          {
            path: 'assigned-requests',
            name: 'AssignedRequests',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                '/taskboard/assigned-requests',
                to,
              );
            },
          },
          {
            path: 'all-requests',
            name: 'AllRequests',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url('/taskboard/all-requests', to);
            },
          },
        ],
      },
      {
        path: 'task-distribution',
        name: 'TaskDistribution',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/task-distribution', to);
        },
      },
      {
        path: 'user-permissions',
        name: 'UserPermissions',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/user-permissions', to);
        },
        meta: { permission: 'isAdmin' },
      },
      {
        path: 'coverage-areas',
        name: 'CoverageAreas',
        beforeEnter: async (to) => {
          window.location.href = mapToV5Url('/coverage-areas', to);
        },
        meta: { permission: 'isAdmin' },
      },
      {
        path: 'asset-types',
        name: 'AssetTypes',
        beforeEnter: async (to) => {
          window.location.href = mapToV5Url('/asset-types', to);
        },
        meta: { permission: 'isAdmin' },
      },
      {
        path: 'warranty-review',
        name: 'WarrantyReview',
        component: WarrantyReviewLayout,
        beforeEnter: async (to, from, next) => {
          const manufacturerStore = useManufacturerStore();
          const assetTypesStore = useAssetTypesStore();
          await Promise.all([
            manufacturerStore.getManufacturersForWarrantyReview(),
            assetTypesStore.getAllAssetTypes(),
          ]);
          next();
        },
        meta: { permission: 'isAdmin' },
      },
      {
        path: 'asset-qr-code-url-generator',
        name: 'AssetQRCodeUrlGenerator',
        beforeEnter: async (to) => {
          window.location.href = mapToV5Url('/asset-qr-code-url-generator', to);
        },
        meta: { permission: 'isAdmin' },
      },
      {
        path: 'ts-guides',
        name: 'TsGuideLayout',
        beforeEnter: async (to) => {
          window.location.href = mapToV5Url('/ts-guides', to);
        },
        meta: { permission: 'isAdmin' },
      },
      {
        path: 'pods',
        name: 'PodManagement',
        beforeEnter: async (to) => {
          window.location.href = mapToV5Url('/pods', to);
        },
        meta: { permission: 'isAdmin' },
      },
      {
        path: 'flex-queues',
        name: 'FlexQueues',
        // meta: { permission: 'isAdmin' },
        redirect: 'flex-queues/emergency',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/flex-queues', to);
        },
        children: [
          {
            path: 'emergency',
            name: 'FlexEmergencyRequestQueue',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url('/flex-queues/emergency', to);
            },
            props: {
              queueType: 'Emergency Priority Requests Queue',
              bucketNames: ['emergency-priority-tickets'],
              isRequestQueue: true,
              showEnterpriseToggle: true,
              enterpriseQueueType: 'EP Emergency Priority Requests Queue',
              enterpriseBucketNames: ['ep-emergency-priority-tickets'],
            },
          },
          {
            path: 'non-emergency',
            name: 'FlexNonEmergencyRequestQueue',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                '/flex-queues/non-emergency',
                to,
              );
            },
            props: {
              queueType: 'Non-Emergency Priority Requests Queue',
              bucketNames: ['non-emergency-priority-tickets'],
              isRequestQueue: true,
              showRequestPriority: true,
              showEnterpriseToggle: true,
              enterpriseQueueType: 'EP Non-Emergency Priority Requests Queue',
              enterpriseBucketNames: ['ep-non-emergency-priority-tickets'],
            },
          },
          {
            path: 'actions',
            name: 'FlexActionsQueue',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url('/flex-queues/actions', to);
            },
            props: {
              queueType: 'Actions',
              bucketNames: ['adhoc-actions', 'stuck-tickets'],
              showEnterpriseToggle: true,
              enterpriseQueueType: 'EP Actions',
              enterpriseBucketNames: ['ep-adhoc-actions'],
            },
          },
          {
            path: 'after-hours',
            name: 'FlexAfterHoursQueue',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url('/flex-queues/after-hours', to);
            },
            props: {
              queueType: 'After Hours',
              bucketNames: ['after-hours-adhoc-actions', 'after-hours'],
            },
          },
        ],
      },
      {
        path: 'new-request',
        name: 'ServiceRequestIntake',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/new-request', to);
        },
      },
      {
        path: 'new-pm-quote',
        name: 'NewPMQuote',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/new-pm-quote', to);
        },
      },
      {
        path: 'request/:id',
        name: 'ServiceRequestDetail',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url(`/request/${to.params.id}`, to);
        },
        children: [
          {
            path: '',
            name: 'RequestDetail',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(`/request/${to.params.id}`, to);
            },
          },
          {
            path: 'activity',
            name: 'RequestActivity',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/activity`,
                to,
              );
            },
          },
          {
            path: 'location',
            name: 'RequestLocation',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/location`,
                to,
              );
            },
          },
          {
            path: 'asset',
            name: 'RequestAsset',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/asset`,
                to,
              );
            },
          },
          {
            path: 'dispatch',
            name: 'RequestDispatch',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/dispatch`,
                to,
              );
            },
          },
          {
            path: 'communication',
            name: 'RequestCommunication',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/communication`,
                to,
              );
            },
          },
          {
            path: 'quote',
            name: 'RequestQuote',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/quote`,
                to,
              );
            },
          },
          {
            path: 'notes',
            name: 'RequestNotes',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/notes`,
                to,
              );
            },
          },
          {
            path: 'work-order',
            name: 'RequestWorkOrder',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/work-order`,
                to,
              );
            },
          },
          {
            path: 'savings',
            name: 'RequestSavings',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/savings`,
                to,
              );
            },
          },
          {
            path: 'attachments',
            name: 'RequestAttachments',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/attachments`,
                to,
              );
            },
          },
          {
            path: 'contract',
            name: 'RequestContract',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/request/${to.params.id}/contract`,
                to,
              );
            },
          },
        ],
      },
      {
        path: 'vendors',
        name: 'Vendors',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/vendors', to);
        },
      },
      {
        path: 'vendor-detail/:id',
        name: 'VendorDetail',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url(`/vendor/${to.params.id}`, to);
        },
      },
      {
        path: 'invoices',
        name: 'InvoicePage',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/invoices', to);
        },
      },
      {
        path: 'invoices/gather',
        name: 'InvoiceGather',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/invoices/gather', to);
        },
      },
      {
        path: 'invoices/transcribe',
        name: 'InvoiceTranscribe',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/invoices/transcribe', to);
        },
      },
      {
        path: 'invoices/transcribe/:id',
        name: 'InvoiceTranscribeById',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url(
            `/invoices/transcribe/${to.params.id}`,
            to,
          );
        },
      },
      {
        path: 'invoices/match',
        name: 'InvoiceMatch',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/invoices/match', to);
        },
      },
      {
        path: 'pm',
        name: 'PmHome',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/pm', to);
        },
      },
      {
        path: 'pm/location/:locationId/',
        name: 'PmLocationDetails',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url(
            `/pm/location/${to.params.locationId}`,
            to,
          );
        },
      },
      {
        path: 'pm/location/:locationId/contract/:contractId',
        name: 'PMContractDetails',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url(
            `/pm/location/${to.params.locationId}/contract/${to.params.contractId}`,
            to,
          );
        },
      },
      {
        path: 'customers',
        name: 'CustomerLayout',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/customers', to);
        },
        children: [
          {
            path: '',
            name: 'CorporateCustomers',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url('/customers', to);
            },
          },
          {
            path: 'locations',
            name: 'Locations',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url('/customers/locations', to);
            },
          },
        ],
      },
      {
        path: 'customers/:customerId',
        name: 'CustomerDetailLayout',
        children: [
          {
            path: '',
            name: 'CustomerDetail',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/customers/${to.params.customerId}`,
                to,
              );
            },
          },
          {
            path: 'locations',
            name: 'CustomerLocations',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/customers/${to.params.customerId}/locations`,
                to,
              );
            },
          },
          {
            path: 'locations/:locationId',
            name: 'CustomerLocationDetail',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/customers/${to.params.customerId}/locations/${to.params.locationId}`,
                to,
              );
            },
          },
          {
            path: 'contacts',
            name: 'CustomerContacts',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/customers/${to.params.customerId}/contacts`,
                to,
              );
            },
          },
          {
            path: 'assets',
            name: 'CustomerAssets',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/customers/${to.params.customerId}/assets`,
                to,
              );
            },
          },
          {
            path: 'assets/:assetId',
            name: 'CustomerAssetDetail',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/customers/${to.params.customerId}/assets/${to.params.assetId}`,
                to,
              );
            },
          },
          {
            path: 'requests',
            name: 'CustomerRequestsList',
            beforeEnter: (to) => {
              window.location.href = mapToV5Url(
                `/customers/${to.params.customerId}/requests`,
                to,
              );
            },
            children: [
              {
                path: '',
                name: 'CustomerRequests',
                beforeEnter: (to) => {
                  window.location.href = mapToV5Url(
                    `/customers/${to.params.customerId}/requests`,
                    to,
                  );
                },
              },
              {
                path: 'escalations',
                name: 'CustomerEscalations',
                beforeEnter: (to) => {
                  window.location.href = mapToV5Url(
                    `/customers/${to.params.customerId}/requests/escalations`,
                    to,
                  );
                },
              },
            ],
          },
        ],
      },
      {
        path: 'brand-owners',
        name: 'BrandOwnerLayout',
        beforeEnter: (to) => {
          window.location.href = mapToV5Url('/brand-owners', to);
        },
      },
    ],
  },
  {
    path: '/s3',
    name: 'S3',
    component: {
      template: '<div>S3</div>',
    },
    beforeEnter: async (to, from, next) => {
      const url = await getSignedUrl(to.query.url, esApiV3);
      window.location.href = url;
      next();
    },
  },
  {
    path: '/flex',
    name: 'Flex',
    beforeEnter: (to) => {
      window.location.href = mapToV5Url('/flex', to);
    },
  },
  {
    path: '/flex/chat-dialer',
    name: 'FlexChatDialer',
    beforeEnter: (to) => {
      window.location.href = mapToV5Url('/flex/chat-dialer', to);
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: import.meta.env.BASE_URL,
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (_to.params.savePosition) {
      return {};
    }
    return { x: 0, y: 0 };
  },
});

async function populateUserAndNav() {
  const authStore = useAuthStore();
  if (!authStore.hasUserInfo) {
    await authStore.getUserInfo();
  }
}

function insufficientPermissionsForRoute(to) {
  const authStore = useAuthStore();
  const routeRequiresAdmin = to.matched.some(
    (record) => record.meta.permission === 'isAdmin',
  );
  return routeRequiresAdmin && !authStore.hasAdminPermission;
}

router.beforeEach(async (to, from, next) => {
  await autoRefreshService.handleNewVersionAvailable();
  analyticsService.logEvent('Page View', {
    Page: to.name,
    Authenticated: authService.hasAuthToken(),
    'Previous Url': from.fullPath,
  });

  if (to.name === 'Login') {
    next();
    return;
  }
  if (!authService.hasAuthToken()) {
    next({
      name: 'Login',
      query: { nextRoute: new URL(window.location.href).toString() },
    });
    return;
  }
  const userPermissionsStore = useUserPermissionsStore();

  if (!userPermissionsStore.hasUsersByPermission) {
    userPermissionsStore.getUsersByPermission('isRequestAssignee');
    userPermissionsStore.getUsersByPermission('isTaskAssignee');
  }
  await populateUserAndNav();
  if (insufficientPermissionsForRoute(to)) {
    next('/');
    return;
  }

  next();
});

export default router;
